/**
 * Pattern-Aware Insight Components
 * Apple-inspired clean design with seasonal patterns and AI recommendations
 */

import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Minus, Calendar, Lightbulb,
  ChevronDown, ChevronUp, Sparkles, ArrowRight, AlertCircle,
  CheckCircle2, Clock, Target, Zap
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getDashboardPatternInsights, getSeasonalPattern } from '@/services/patternInsightsApi';

/**
 * Enhanced Insight Summary Card with Pattern Recognition
 * Apple-inspired clean, minimal design
 */
export const PatternAwareInsightCard = ({
  userName,
  businessId,
  businessName,
  industry = 'hospitality',
  metrics = {},
  lastLogin,
  sources = [],
}) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!businessId || !businessName) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getDashboardPatternInsights(
          businessId,
          businessName,
          industry,
          metrics
        );
        setInsights(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch pattern insights:', err);
        setError('Unable to load insights');
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  // eslint-disable-next-line
  }, [businessId, businessName, industry]);

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getFirstName = () => {
    if (!userName) return '';
    return userName.split(' ')[0];
  };

  const formatLastLogin = () => {
    if (!lastLogin) return 'first time here';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'earlier today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Get performance context message - Sharp, direct, progression-focused
  const getPerformanceContext = () => {
    // Extract actual metrics
    const revenueChange = metrics?.revenue_change_pct || metrics?.revenue_change || 0;
    const customerChange = metrics?.customer_change_pct || metrics?.customer_change || 0;
    const revenue = metrics?.revenue || metrics?.revenue_current || 0;
    
    // Format currency
    const formatRevenue = (val) => {
      if (val >= 1000000) return `$${(val/1000000).toFixed(1)}M`;
      if (val >= 1000) return `$${(val/1000).toFixed(0)}K`;
      return `$${val}`;
    };
    
    // Build sharp, direct message based on actual performance
    if (revenueChange > 10) {
      return `Revenue up ${revenueChange.toFixed(0)}% — you're ahead of pace. Let's keep it going.`;
    } else if (revenueChange > 0) {
      return `${formatRevenue(revenue)} revenue this period, +${revenueChange.toFixed(0)}%. Solid progress.`;
    } else if (revenueChange < -5) {
      return `Revenue down ${Math.abs(revenueChange).toFixed(0)}%. Three actions below to get back on track.`;
    } else if (customerChange > 5) {
      return `Customer traffic up ${customerChange.toFixed(0)}%. Now let's convert that to revenue.`;
    } else if (customerChange < -5) {
      return `Customer visits down ${Math.abs(customerChange).toFixed(0)}%. Focus areas flagged below.`;
    }
    
    // Default: something actionable
    return `Here's what needs your attention today.`;
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-100 rounded-lg w-2/3"></div>
          <div className="h-4 bg-gray-50 rounded w-1/2"></div>
          <div className="h-24 bg-gray-50 rounded-xl"></div>
        </div>
      </div>
    );
  }

  // Fallback if no insights available
  if (!insights) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">
          {getTimeGreeting()}, {getFirstName() || 'there'}
        </h2>
        <p className="text-gray-500 mt-1">
          {businessName && <span>{businessName} • </span>}
          Last visit: {formatLastLogin()}
        </p>
        <p className="text-gray-600 mt-4">Your business insights are being prepared...</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-6 sm:p-8 shadow-sm">
      {/* Header - Clean greeting with name */}
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">
          {getTimeGreeting()}, {getFirstName() || 'there'}
        </h2>
        
        {/* Performance context in smaller text */}
        {getPerformanceContext() && (
          <p className="text-sm text-gray-500 mt-1">
            {getPerformanceContext()}
          </p>
        )}
        
        {/* Business name and last login */}
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
          {businessName && <span>{businessName}</span>}
          {businessName && <span>•</span>}
          <span>Last visit: {formatLastLogin()}</span>
          {sources.length > 0 && (
            <>
              <span>•</span>
              <span>Data from {sources.length} source{sources.length > 1 ? 's' : ''}</span>
            </>
          )}
        </div>
      </div>
      
      {/* Key Metrics - Clean cards */}
      {insights.key_insights?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {insights.key_insights.map((insight, idx) => (
            <div 
              key={idx} 
              className="rounded-xl bg-gray-50 p-4 border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">{insight.metric_name}</span>
                {insight.is_outperforming ? (
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px] font-medium">
                    On Track
                  </Badge>
                ) : insight.change_direction === 'down' ? (
                  <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px] font-medium">
                    Needs Focus
                  </Badge>
                ) : null}
              </div>
              
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-2xl font-semibold ${
                  insight.change_direction === 'up' ? 'text-emerald-600' : 
                  insight.change_direction === 'down' ? 'text-red-500' : 'text-gray-900'
                }`}>
                  {insight.change_percent > 0 ? '+' : ''}{insight.change_percent}%
                </span>
                <span className="text-sm text-gray-400">vs last period</span>
              </div>
              
              <p className="text-xs text-gray-500 leading-relaxed">
                {insight.pattern_context}
              </p>
              
              {insight.prediction && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
                  <Target className="w-3 h-3" />
                  {insight.prediction}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Upcoming Trend - Subtle highlight */}
      {insights.upcoming_trend && (
        <div className="rounded-xl bg-blue-50/50 border border-blue-100 p-4 mb-6">
          <p className="text-sm text-gray-700">{insights.upcoming_trend}</p>
        </div>
      )}
      
      {/* Expandable AI Insight & Tips */}
      <div className="border-t border-gray-100 pt-4">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="font-medium">AI Insights & Proactive Tips</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {expanded && (
          <div className="mt-4 space-y-4">
            {/* AI Generated Insight */}
            {insights.ai_generated_insight && (
              <div className="rounded-xl bg-amber-50/50 border border-amber-100 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-700">AI Analysis</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{insights.ai_generated_insight}</p>
              </div>
            )}
            
            {/* Proactive Tips */}
            {insights.proactive_tips?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-gray-600">Proactive Tips</span>
                </div>
                <div className="space-y-2">
                  {insights.proactive_tips.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-blue-400 flex-shrink-0" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Confidence indicator - Minimal footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${
            insights.pattern_confidence === 'high' ? 'bg-emerald-400' : 
            insights.pattern_confidence === 'medium' ? 'bg-amber-400' : 'bg-gray-300'
          }`} />
          <span className="text-[11px] text-gray-400">
            {insights.pattern_confidence === 'high' ? 'High' : 
             insights.pattern_confidence === 'medium' ? 'Medium' : 'Low'} confidence
          </span>
        </div>
        <span className="text-[11px] text-gray-300">
          {new Date(insights.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

/**
 * Enhanced Win Card with Pattern Context
 * Apple-inspired clean design
 */
export const PatternAwareWinCard = ({
  title,
  description,
  metric,
  metricLabel,
  patternContext,
  prediction,
  isOutperforming,
  recommendations = [],
  sources = [],
  timeframe = 'this week',
  icon: Icon = TrendingUp,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Card className="rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="p-2.5 rounded-xl bg-emerald-50">
            <Icon className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <h4 className="font-semibold text-gray-900">{title}</h4>
              {isOutperforming && (
                <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">
                  Outperforming
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
            
            {/* Metric display */}
            <div className="flex items-baseline gap-2 mt-3">
              <span className="text-2xl font-semibold text-emerald-600">{metric}</span>
              <span className="text-sm text-gray-400">{metricLabel}</span>
            </div>
            
            {/* Pattern context */}
            {patternContext && (
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                <Calendar className="w-3 h-3 text-gray-400" />
                {patternContext}
              </p>
            )}
            
            {/* Prediction */}
            {prediction && (
              <p className="text-xs text-amber-600 mt-2">
                💡 {prediction}
              </p>
            )}
            
            {/* Expandable recommendations */}
            {recommendations.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <button 
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  {showDetails ? 'Hide' : 'Show'} recommendations
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                
                {showDetails && (
                  <div className="mt-2 space-y-1.5">
                    {recommendations.slice(0, 3).map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Footer */}
            <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-400">
              <Clock className="w-3 h-3" />
              <span>{timeframe}</span>
              {sources.length > 0 && (
                <>
                  <span>•</span>
                  <span>{sources.slice(0, 2).join(', ')}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Seasonal Outlook Banner
 * Apple-inspired minimal design
 */
export const SeasonalOutlookBanner = ({ industry = 'hospitality' }) => {
  const [pattern, setPattern] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPattern = async () => {
      try {
        const data = await getSeasonalPattern(industry);
        setPattern(data);
      } catch (err) {
        console.error('Failed to fetch seasonal pattern:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPattern();
  }, [industry]);

  if (loading || !pattern) return null;

  const nextMonth = pattern.next_month;
  const isPositive = nextMonth.typical_trend === 'up';
  const isNegative = nextMonth.typical_trend === 'down';

  return (
    <div className="rounded-xl bg-white border border-gray-100 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${
          isPositive ? 'bg-emerald-50' : isNegative ? 'bg-amber-50' : 'bg-gray-50'
        }`}>
          {isPositive ? (
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          ) : isNegative ? (
            <AlertCircle className="w-4 h-4 text-amber-600" />
          ) : (
            <Target className="w-4 h-4 text-gray-500" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">
              {nextMonth.month_name} Outlook
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isPositive ? 'bg-emerald-50 text-emerald-600' :
              isNegative ? 'bg-amber-50 text-amber-600' :
              'bg-gray-100 text-gray-500'
            }`}>
              {nextMonth.typical_change_percent > 0 ? '+' : ''}{nextMonth.typical_change_percent}% typical
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{nextMonth.description}</p>
        </div>
      </div>
      
      {nextMonth.recommendations?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap gap-2">
          {nextMonth.recommendations.slice(0, 2).map((rec, idx) => (
            <span key={idx} className="text-[11px] text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
              {rec}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatternAwareInsightCard;
